import React, { useState } from 'react';
import { Button, message, Modal } from 'antd';
import { CopyOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface OnboardingDocumentDownloadModalProps {
    visible: boolean;
    onClose: () => void;
    fileName: string;
}

const OnboardingDocumentDownloadModal: React.FC<OnboardingDocumentDownloadModalProps> = ({
    visible,
    onClose,
    fileName,
}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [copyLoading, setCopyLoading] = useState(false);

    const handleCopyFileName = async () => {
        setCopyLoading(true);
        try {
            await navigator.clipboard.writeText(fileName);
            message.success(t('central.channels.onboardingDownloadModal.copySuccess'));
        } catch {
            message.error(t('central.channels.onboardingDownloadModal.copyFailed'));
        } finally {
            setCopyLoading(false);
        }
    };

    const handleGoToDownloadPage = () => {
        if (location.pathname.includes('/qualitycontrol')) {
            navigate('/qualitycontrol/download');
        } else {
            navigate('/central/download');
        }
        onClose();
    };

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="w-1 h-6 bg-purple-500 mr-3" />
                    <span className="text-lg font-medium">
                        {t('central.channels.onboardingDownloadModal.title')}
                    </span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={600}
            footer={[
                <Button key="close" onClick={onClose}>
                    {t('components.button.close')}
                </Button>,
                <Button key="copy" icon={<CopyOutlined />} loading={copyLoading} onClick={handleCopyFileName}>
                    {t('components.button.copyFilename')}
                </Button>,
                <Button key="download" type="primary" onClick={handleGoToDownloadPage}>
                    {t('components.button.goToDownloadPage')}
                </Button>,
            ]}
        >
            <div className="py-4">
                <p className="text-gray-800 font-medium mb-4">
                    {t('central.channels.onboardingDownloadModal.description')}
                </p>
                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <span className="font-medium">{t('delivery.parcels.label.filename')}: </span>
                    <code className="break-all">{fileName}</code>
                </div>
            </div>
        </Modal>
    );
};

export default OnboardingDocumentDownloadModal;
